/* eslint-disable @typescript-eslint/no-non-null-assertion,@typescript-eslint/consistent-type-assertions */
import { css, cx } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, SearchInput, Pagination, Divider, Button, Collapse, useIsLargerThanIpadResolution } from "@octopusdeploy/design-system-components";
import { FilterIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentsSummaryV2Resource, EnvironmentV2Resource, GetEnvironmentsSummaryRequestV2, TenantResource, TagSetScope } from "@octopusdeploy/octopus-server-client";
import { mapFromEnvironmentResourceToEnvironmentV2Resource, Repository, Permission, mapFromEnvironmentSummaryResourceToEnvironmentSummaryV2Resource } from "@octopusdeploy/octopus-server-client";
import type { MachineHealthStatus, QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { compact, isEqual } from "lodash";
import { useEffect, useState } from "react";
import * as React from "react";
import { Action as AnalyticAction, useAnalyticActionDispatch } from "~/analytics/Analytics";
import type { EndpointRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import endpointRegistry from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { FilterBuilder } from "~/areas/tenants/components/Filtering/FilterBuilder/FilterBuilder";
import type { FilterValue, Filter } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import { FilterEntity, FilterOption, FilterType, FilterMultiplicity, getIncludedEnvironmentValues, getIncludedTenantValues, getIncludedRoleValues, getIncludedMachineHealthValues, getIncludedDeploymentTargetTypeValues, getIncludedShellValues, createInclusiveOnlyEnvironmentFilter, createInclusiveOnlyTenantFilter, createInclusiveOnlyRoleFilter, createInclusiveOnlyMachineHealthFilter, createInclusiveOnlyDeploymentTargetTypeFilter, createInclusiveOnlyShellFilter, } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import { VerticalDivider } from "~/areas/tenants/components/HeaderBar/CollapsibleFilter";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout";
import { AdvancedFilterTextInput } from "~/components/AdvancedFilterLayout/Text/AdvancedFilterTextInput";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import ExpansionButtons from "~/components/form/Sections/ExpansionButtons";
import type { TagIndex, TagsLookup } from "~/components/tagSets";
import * as tagSets from "~/components/tagSets";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import { isAllowed } from "../../../../components/PermissionCheck/PermissionCheck";
import ConfirmTentacleUpgradePanel from "../MachinesLayout/ConfirmTentacleUpgradePanel";
import { AddEnvironmentsDialog } from "./AddEnvironmentsDialog";
import { createEnvironmentSummaryArgs, defaultEnvironmentSummaryFilter } from "./EnvironmentSummaryFilter";
import EnvironmentSummarySection from "./EnvironmentSummarySection";
import EnvironmentsSorter from "./EnvironmentsSorter";
import { FilteredByChips } from "./FilterSelection";
export interface EnvironmentsPageQueryParams {
    environmentIds: string[];
    roles: string[];
    tenantIds: string[];
    tenantTags: string[];
    environmentTags: string[];
    shellNames: string[];
    deploymentTargetTypes: string[];
    hideEmptyEnvironments: boolean;
    machinePartialName: string | undefined;
    isDisabled: boolean | undefined;
    healthStatuses: MachineHealthStatus[];
    partialName: string | undefined;
}
interface EnvironmentsPageProps {
    spaceId: string;
    loaderData: EnvironmentsPageLoaderData;
    queryParams: EnvironmentsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<EnvironmentsPageQueryParams>;
}
export interface EnvironmentsPageLoaderData {
    environmentsSummary: EnvironmentsSummaryV2Resource;
    environments: EnvironmentV2Resource[];
    machineRoles: string[];
    tenants: TenantResource[];
    tagIndex: TagIndex;
    tagsLookup: TagsLookup;
    machineShellNames: string[];
    endpointRegistrations: EndpointRegistration[];
    ephemeralEnvironmentsAreEnabled: boolean;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const Title = "Environments";
async function getEnvironmentsV2Summary(args: GetEnvironmentsSummaryRequestV2, ephemeralEnvironmentsAreEnabled: boolean, repository: Repository): Promise<EnvironmentsSummaryV2Resource> {
    if (ephemeralEnvironmentsAreEnabled) {
        return await repository.Environments.summaryV2({ ...args, type: ["Static", "Parent"] });
    }
    else {
        const environmentsSummaryResponse = await repository.Environments.summary(args);
        return mapFromEnvironmentSummaryResourceToEnvironmentSummaryV2Resource(environmentsSummaryResponse);
    }
}
export async function environmentsPageLoader(repository: Repository, queryParams: EnvironmentsPageQueryParams, ephemeralEnvironmentsAreEnabled: boolean, extendedTagSetsEnabled: boolean): Promise<EnvironmentsPageLoaderData> {
    let environments: EnvironmentV2Resource[] = [];
    if (ephemeralEnvironmentsAreEnabled) {
        const environmentsV2Response = await repository.Environments.getV2({ spaceId: repository.spaceId!, skip: 0, take: Repository.takeAll, type: ["Static", "Parent"] });
        environments = environmentsV2Response.Items;
    }
    else {
        const environmentsResponse = await repository.Environments.all();
        environments = environmentsResponse.map(mapFromEnvironmentResourceToEnvironmentV2Resource);
    }
    const allTagSets = extendedTagSetsEnabled ? await repository.TagSets.getAllByScopes(["Tenant", "Environment"]) : await repository.TagSets.getAllByScopes(["Tenant"]);
    const filteredTagSets = extendedTagSetsEnabled ? allTagSets : allTagSets.map((ts) => ({ ...ts, Scopes: ts.Scopes?.filter((scope) => scope === "Tenant") }));
    const tagIndex = tagSets.getTagIndexForTagSets(filteredTagSets);
    const setLookup = filteredTagSets.reduce((lookup, ts) => lookup.set(ts.Id, ts), new Map());
    const tagLookup = filteredTagSets
        .flatMap((ts) => ts.Tags.map((tag) => ({ tag, set: ts })))
        .reduce((lookup, item) => {
        lookup.set(item.tag.Id, item);
        if (item.tag.CanonicalTagName) {
            lookup.set(item.tag.CanonicalTagName, item);
        }
        return lookup;
    }, new Map());
    const tagsLookup = { tagSets: setLookup, tags: tagLookup };
    const args = await createEnvironmentSummaryArgs(queryParams, tagIndex);
    const environmentsSummaryPromise = getEnvironmentsV2Summary(args, ephemeralEnvironmentsAreEnabled, repository);
    const machineRolesPromise = repository.MachineRoles.all();
    const tenantsPromise = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
    const machineShellNamesPromise = repository.MachineShells.all();
    return {
        environmentsSummary: await environmentsSummaryPromise,
        environments,
        machineRoles: await machineRolesPromise,
        tenants: await tenantsPromise,
        tagIndex,
        tagsLookup,
        machineShellNames: await machineShellNamesPromise,
        endpointRegistrations: await endpointRegistry.getAllRegistrations(),
        ephemeralEnvironmentsAreEnabled,
    };
}
export function EnvironmentsPage({ queryParams, setQueryParams, spaceId, loaderData }: EnvironmentsPageProps) {
    const dispatchAction = useAnalyticActionDispatch();
    const addEnvironmentDialogControls = useDialogTrigger();
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const { doBusyTask, status } = useLegacyDoBusyTask();
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(queryParams.partialName, (value) => setQueryParams((prev) => ({ ...prev, partialName: value })), 200);
    const machineHealthStatuses = React.useMemo(() => MachineHealthStatusHelper.getMachineModelHealthStatusResources(), []);
    const environmentFilter = loaderData.environments?.length > 0 ? createInclusiveOnlyEnvironmentFilter(loaderData.environments, queryParams.environmentIds) : null;
    const tenantFilter = loaderData.tenants?.length > 0 ? createInclusiveOnlyTenantFilter(loaderData.tenants, queryParams.tenantIds) : null;
    const roleFilter = loaderData.machineRoles?.length > 0 ? createInclusiveOnlyRoleFilter(loaderData.machineRoles, queryParams.roles) : null;
    const machineHealthFilter = machineHealthStatuses?.length > 0
        ? createInclusiveOnlyMachineHealthFilter(machineHealthStatuses.map((status) => ({ Id: status.Id, Name: status.Name })), queryParams.healthStatuses)
        : null;
    const deploymentTargetTypeFilter = loaderData.endpointRegistrations?.length > 0
        ? createInclusiveOnlyDeploymentTargetTypeFilter(loaderData.endpointRegistrations.map((reg) => ({ Id: reg.key, Name: reg.name })), queryParams.deploymentTargetTypes)
        : null;
    const shellFilter = loaderData.machineShellNames?.length > 0 ? createInclusiveOnlyShellFilter(loaderData.machineShellNames, queryParams.shellNames) : null;
    const createTagFilters = (scope: TagSetScope, selectedTagIds: string[]) => {
        const tagSetsByName = loaderData.tagsLookup?.tagSets ? Array.from(loaderData.tagsLookup.tagSets.values()).filter((ts) => ts.Scopes?.includes(scope) && ts.Type !== "FreeText") : [];
        return tagSetsByName.map((tagSet) => {
            const tagIdsInTagSet = tagSet.Tags.map((t) => t.Id);
            const tagIdsInIncludeFilter = selectedTagIds.filter((t) => tagIdsInTagSet.includes(t));
            return {
                entity: FilterEntity.TagSet,
                options: [{ option: FilterOption.In as const, initialValue: tagIdsInIncludeFilter.length > 0 ? tagIdsInIncludeFilter : undefined }],
                multiplicity: FilterMultiplicity.OncePerOption,
                availableItems: tagSet.Tags,
                name: `${tagSet.Name} (${scope})`,
            };
        });
    };
    const tenantTagSetsByName = React.useMemo(() => loaderData.tagsLookup?.tagSets
        ? new Map(Array.from(loaderData.tagsLookup.tagSets.values())
            .filter((ts) => ts.Scopes?.includes("Tenant") && ts.Type !== "FreeText")
            .map((ts) => [ts.Name, ts]))
        : new Map(), [loaderData.tagsLookup?.tagSets]);
    const tenantTagFilters = createTagFilters("Tenant", queryParams.tenantTags || []);
    const environmentTagSetsByName = React.useMemo(() => loaderData.tagsLookup?.tagSets
        ? new Map(Array.from(loaderData.tagsLookup.tagSets.values())
            .filter((ts) => ts.Scopes?.includes("Environment") && ts.Type !== "FreeText")
            .map((ts) => [ts.Name, ts]))
        : new Map(), [loaderData.tagsLookup?.tagSets]);
    const environmentTagFilters = createTagFilters("Environment", queryParams.environmentTags || []);
    const filterBuilderFilters = [environmentFilter, tenantFilter, roleFilter, machineHealthFilter, deploymentTargetTypeFilter, shellFilter, ...tenantTagFilters, ...environmentTagFilters].filter((filter) => filter !== null);
    const handleFilterChange = React.useCallback((filterValues: FilterValue[]) => {
        const allTagValues = filterValues.filter((f) => f.entity === FilterEntity.TagSet && f.option === FilterOption.In);
        const getBaseName = (name: string) => name.replace(/ \((Tenant|Environment)\)$/, "");
        const tenantTags = compact(allTagValues
            .filter((f) => {
            const baseName = getBaseName(f.name);
            const tagSet = tenantTagSetsByName.get(baseName);
            if (!tagSet)
                return false;
            const hasMultipleScopes = tagSet.Scopes && tagSet.Scopes.length > 1;
            return !hasMultipleScopes || f.name.endsWith(" (Tenant)");
        })
            .flatMap((f) => (f.value.type === FilterType.MultiSelect ? f.value.value : undefined)));
        const environmentTags = compact(allTagValues
            .filter((f) => {
            const baseName = getBaseName(f.name);
            const tagSet = environmentTagSetsByName.get(baseName);
            if (!tagSet)
                return false;
            const hasMultipleScopes = tagSet.Scopes && tagSet.Scopes.length > 1;
            return !hasMultipleScopes || f.name.endsWith(" (Environment)");
        })
            .flatMap((f) => (f.value.type === FilterType.MultiSelect ? f.value.value : undefined)));
        setQueryParams((prev) => ({
            ...prev,
            environmentIds: getIncludedEnvironmentValues(filterValues),
            tenantIds: getIncludedTenantValues(filterValues),
            roles: getIncludedRoleValues(filterValues),
            healthStatuses: getIncludedMachineHealthValues(filterValues),
            deploymentTargetTypes: getIncludedDeploymentTargetTypeValues(filterValues),
            shellNames: getIncludedShellValues(filterValues),
            tenantTags,
            environmentTags,
        }));
    }, [setQueryParams, tenantTagSetsByName, environmentTagSetsByName]);
    const handleToggleFilters = () => {
        setFiltersExpanded(!filtersExpanded);
        dispatchAction("Toggle Filters", { resource: "Filters", action: AnalyticAction.View });
    };
    const navigate = useSpaceAwareNavigation();
    const { result: environmentsSummary, refetch: reloadEnvironmentSummaries } = useQuery(async (repository) => {
        const args = await createEnvironmentSummaryArgs(queryParams, loaderData.tagIndex);
        return getEnvironmentsV2Summary(args, loaderData.ephemeralEnvironmentsAreEnabled, repository);
    }, [queryParams, loaderData.tagIndex, loaderData.ephemeralEnvironmentsAreEnabled], "Load Environment Summaries", { initialResult: loaderData.environmentsSummary });
    useEffect(() => {
        setCurrentPageIndex(0);
    }, [environmentsSummary]);
    const addDeploymentTargetPageAction: PageAction = {
        type: "navigate",
        label: "Add Deployment Target",
        path: links.selectDeploymentTargetTypePage.generateUrl({ spaceId }),
        buttonType: "secondary",
        hasPermissions: isAllowed({
            permission: Permission.MachineCreate,
            environment: "*",
            tenant: "*",
        }),
    };
    const addEnvironmentClicked = () => {
        dispatchAction("Add Environment", { resource: "Environment", action: AnalyticAction.Add });
        addEnvironmentDialogControls.openDialog();
    };
    const hasEnvironmentCreatePermission = isAllowed({
        permission: Permission.EnvironmentCreate,
        environment: "*",
    });
    const addEnvironmentPageAction: PrimaryPageAction = {
        type: "button",
        label: "Add Environment",
        hasPermissions: hasEnvironmentCreatePermission,
        onClick: addEnvironmentClicked,
    };
    const legacyOverflowMenuReorderItem = OverflowMenuItems.dialogItem("Reorder", <EnvironmentsSorter saveDone={reloadEnvironmentSummaries}/>, { permission: Permission.EnvironmentEdit, environment: "*" });
    const overflowMenu = OverflowMenuConverterVNext.convertAll([legacyOverflowMenuReorderItem]);
    const environmentSummaries = environmentsSummary.EnvironmentSummaries;
    const hasAvailableEnvironments = loaderData.environments && loaderData.environments.length > 0;
    const start = currentPageIndex * pageSize;
    const end = start + pageSize;
    const currentPageEnvironmentSummaries = environmentSummaries.slice(start, end);
    const pagination = (<Pagination currentPage={currentPageIndex + 1} itemsPerPageOptions={pageSizeOptions} label="Environments" onPageChange={(page) => setCurrentPageIndex(page - 1)} selectedItemsPerPage={pageSize} onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} totalResults={environmentsSummary.EnvironmentSummaries.length}/>);
    const environmentExpanders = currentPageEnvironmentSummaries.map((environmentsSummary) => (<React.Fragment key={environmentsSummary.Environment.Id}>
            <Divider />
            <EnvironmentSummarySection endpointRegistrations={loaderData.endpointRegistrations} environmentSummary={environmentsSummary} filter={queryParams} isFiltering={isFiltering(queryParams)} tenants={loaderData.tenants} tagIndex={loaderData.tagIndex} tagsLookup={loaderData.tagsLookup}/>
        </React.Fragment>));
    const filters = {
        inputs: [
            <EnvironmentsPageFilterSection key="environments-filter-section" queryParams={queryParams} setQueryParams={setQueryParams} filterBuilderFilters={filterBuilderFilters} handleFilterChange={handleFilterChange} filtersExpanded={filtersExpanded} onToggleFilters={handleToggleFilters} debouncedPartialName={debouncedPartialName} setDebouncedPartialName={setDebouncedPartialName} pagination={pagination} loaderData={loaderData}/>,
        ],
    };
    const tentacleUpgradesRequiredWarning = environmentSummaries.some((x) => x.TentacleUpgradesRequired) && <ConfirmTentacleUpgradePanel doBusyTask={doBusyTask} onTentacleUpgradeComplete={() => navigate.navigate(links.tasksPage.generateUrl())}/>;
    const dialogs = (<>
            <Dialog open={addEnvironmentDialogControls.isOpen}>
                <AddEnvironmentsDialog saveDone={async (env) => {
            addEnvironmentDialogControls.closeAllDialogs();
            env.Type === "Parent" ? navigate.navigate(links.parentEnvironmentPage.generateUrl({ spaceId, environmentId: env.Id })) : navigate.navigate(links.environmentPage.generateUrl({ spaceId, environmentId: env.Id }));
        }}/>
            </Dialog>
        </>);
    if (!hasAvailableEnvironments) {
        return (<>
                <PortalEmptyStateOnboarding title={"Add environments to group the deployment targets you will be deploying to"} description={"Environments group deployment targets so your software can be promoted through your deployment lifecycle. For instance, from Development to Test and finally into Production."} learnMore={<>
                            <ExternalLink href="OnboardingEnvironmentsLearnMore">Learn more</ExternalLink>
                            <ExternalLink href="OnboardingEnvironmentsVideo">Video (2 mins)</ExternalLink>
                        </>} primaryAction={addEnvironmentPageAction}/>
                {dialogs}
            </>);
    }
    return (<PageContent legacyStatus={status} header={{
            title: Title,
            primaryAction: addEnvironmentPageAction,
            pageActions: [addDeploymentTargetPageAction],
            overflowActions: overflowMenu.menuItems,
        }} filters={filters} pagination={currentPageEnvironmentSummaries.length > 20
            ? {
                ui: pagination,
                placement: "bottom",
            }
            : undefined} callout={{ type: "custom", content: <>{tentacleUpgradesRequiredWarning}</> }}>
            <BorderedContent>
                {overflowMenu.dialogs}
                {dialogs}
                {environmentExpanders.length > 0 && (<div className={expansionButtonsContainerStyles}>
                        <ExpansionButtons />
                    </div>)}
                {environmentExpanders.length > 0 ? environmentExpanders : <EmptyStateNoResults />}
            </BorderedContent>
        </PageContent>);
}
interface EnvironmentsPageFilterSectionProps {
    queryParams: EnvironmentsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<EnvironmentsPageQueryParams>;
    filterBuilderFilters: Filter[];
    handleFilterChange: (filterValues: FilterValue[]) => void;
    filtersExpanded: boolean;
    onToggleFilters: () => void;
    debouncedPartialName?: string;
    setDebouncedPartialName: (value: string) => void;
    pagination: React.ReactNode;
    loaderData: EnvironmentsPageLoaderData;
}
function EnvironmentsPageFilterSection({ queryParams, setQueryParams, filterBuilderFilters, handleFilterChange, filtersExpanded, onToggleFilters, debouncedPartialName, setDebouncedPartialName, pagination, loaderData, }: EnvironmentsPageFilterSectionProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    return (<section className={filterSectionStyles.filterSection} aria-label="environment-filter-selection">
            <div className={cx(filterSectionStyles.filterControls, { [filterSectionStyles.filterControlsMobile]: !isLargerThanIpad })}>
                <div className={filterSectionStyles.filterControlsLeft}>
                    <div className={filterSectionStyles.searchInputContainer}>
                        <SearchInput placeholder="Filter by environment name..." value={debouncedPartialName ?? ""} onChange={setDebouncedPartialName} autoFocus={true} accessibleName="Filter by environment name"/>
                    </div>
                    <Button label={`${filtersExpanded ? "Hide" : "Show"} advanced filters`} icon={<FilterIcon size={20}/>} onClick={onToggleFilters} importance="quiet"/>
                    <FilteredByChips queryParams={queryParams} loaderData={loaderData}/>
                </div>
                <div>{pagination}</div>
            </div>
            <Collapse in={filtersExpanded} timeout="auto" unmountOnExit={true}>
                <div className={filterSectionStyles.advancedFiltersContainer}>
                    <div className={filterSectionStyles.filterBuilder}>
                        <EnvironmentStaticFilters queryParams={queryParams} setQueryParams={setQueryParams}/>

                        <VerticalDivider />

                        {filterBuilderFilters.length > 0 &&
            (isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? (<FilterBuilder filters={filterBuilderFilters} onChange={handleFilterChange} display="full"/>) : (<FilterBuilder filters={filterBuilderFilters.filter((f) => f.entity !== FilterEntity.Tenant && !f.name.toLowerCase().includes("tenant"))} onChange={handleFilterChange} display="full"/>))}
                    </div>
                </div>
            </Collapse>
        </section>);
}
interface EnvironmentStaticFiltersProps {
    queryParams: EnvironmentsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<EnvironmentsPageQueryParams>;
}
function EnvironmentStaticFilters({ queryParams, setQueryParams }: EnvironmentStaticFiltersProps) {
    return (<div className={filterSectionStyles.staticFilters}>
            <div className={filterSectionStyles.displayOptionsTitle}>Show</div>
            <AdvancedFilterCheckbox label="Disabled only" value={queryParams.isDisabled === true} onChange={(x) => setQueryParams((prev) => ({ ...prev, isDisabled: x ? x : undefined }))}/>
            <AdvancedFilterCheckbox label="Hide empty environments" value={queryParams.hideEmptyEnvironments} onChange={(x) => setQueryParams((prev) => ({ ...prev, hideEmptyEnvironments: x }))}/>
            <AdvancedFilterTextInput fieldName="Filter by deployment target..." value={queryParams.machinePartialName ?? ""} onChange={(x) => setQueryParams((prev) => ({ ...prev, machinePartialName: x }))}/>
        </div>);
}
function isFiltering(queryParams: EnvironmentsPageQueryParams): boolean {
    return !isEqual(queryParams, defaultEnvironmentSummaryFilter);
}
const expansionButtonsContainerStyles = css({
    paddingTop: space[16],
    paddingBottom: space[4],
    paddingLeft: space[16],
});
const filterSectionStyles = {
    filterSection: css({
        width: "100%",
    }),
    filterControls: css({
        display: "flex",
        justifyContent: "space-between",
        gap: space[8],
    }),
    filterControlsMobile: css({
        flexWrap: "wrap",
    }),
    filterControlsLeft: css({
        display: "flex",
        gap: space[16],
        flexWrap: "wrap",
    }),
    searchInputContainer: css({
        width: "19rem",
    }),
    advancedFiltersContainer: css({
        paddingTop: space[16],
    }),
    filterBuilder: css({
        display: "flex",
        gap: "0.6rem",
        padding: space[16],
    }),
    staticFilters: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
        minWidth: "280px",
        flexShrink: 0,
        paddingRight: space[8],
    }),
    displayOptionsTitle: css({
        color: themeTokens.color.text.disabled,
    }),
};
